'use client';

import { useQuery } from '@tanstack/react-query';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FaArrowLeftLong } from 'react-icons/fa6';
import { When } from 'react-if';

import cn from '@/lib/clsxm';

import Button from '@/components/@base/button';
import Container from '@/components/@base/container';
import { showToast } from '@/components/@base/toast/show-toast';
import { usePostAddToCart, useRemoveFromCart } from '@/components/app/cart/services/hooks';
import {
  getDefaultPricing,
  getLifetimePricing,
  getOneYearSubscriptionPricing,
  isBundle,
} from '@/components/app/product/helpers';
import {
  getSingleProductOptions,
  useGetAddOnsInfo,
} from '@/components/app/product/services/hooks';
import OptionsItem from '@/components/shared/components/add-to-cart-button/add-to-cart-modal/options-item';
import OptionsLoading from '@/components/shared/components/add-to-cart-button/add-to-cart-modal/options-item/loading';
import ProductSummary from '@/components/shared/components/add-to-cart-button/add-to-cart-modal/product-summary';
import ProductSummaryLoading from '@/components/shared/components/add-to-cart-button/add-to-cart-modal/product-summary/loading';
import Props, {
  ADDONS_ENUM,
} from '@/components/shared/components/add-to-cart-button/add-to-cart-modal/type';
import { isAddonDisabled } from '@/components/shared/components/add-to-cart-button/helper';
import SmartInLineCart from '@/components/shared/components/smart-offers/inline-cart';
import { useGetSmartOffer } from '@/components/shared/components/smart-offers/services/hooks';
import SteppedDiscount from '@/components/shared/components/stepped-discount/components';

import { useAddonsStore } from '@/store/addons.store';
import { usePrerequisitesStore } from '@/store/prerequisites.store';
import { useSmartOfferStore } from '@/store/smart-offer.store';

import {
  DATA_CY_ADD_TO_CARD_POP_UP,
  DATA_CY_OPTIONS_ITEM_CONTAINER,
  DATA_CY_RETURN_BUTTON,
} from '@/constant/cypress';
import ICON_SIZE from '@/constant/icon-size-color';
import { pageLevelLocalization } from '@/constant/localization';

const { product: productLocalization } = pageLevelLocalization;

export default function AddToCartModal({
  slug,
  productId,
  demoId,
  steppedDiscounts,
  cart,
  onClose,
}: Props) {
  const [steppedDiscount, setSteppedDiscount] = useState(steppedDiscounts);
  const [initialAddonsDisplay, setInitialAddonsDisplay] = useState(false);
  // A ref, not state: it's only ever read inside debounced callbacks that may fire well after
  // the render that scheduled them. A state value would be captured stale by that closure
  // (frozen at whatever it was when the debounce instance was created), silently reintroducing
  // the same "blocked call gets dropped/misfires" race the pending-update queue below fixes.
  const isUpdatingAddonsRef = useRef(false);
  const { addons, setAddons } = useAddonsStore();
  const { prerequisites, setPrerequisites } = usePrerequisitesStore();
  const { data, isPending } = useGetAddOnsInfo({ slug, demoId });
  const { data: productData } = useQuery(getSingleProductOptions(slug));
  const defaultPricing = productData ? getDefaultPricing(productData) : null;
  // A bundle's discount isn't time-boxed the way a real sale is (it's the structural gap between
  // the sum of its items and the bundle price), so `saleEndedAt` isn't a valid gate for it —
  // mirrors PriceInfo's `showDiscountWithoutExpiration={isBundle(data)}` on the product page
  // (price-info/index.tsx). `data` here (`AddToCartModel`) has no `market`/`bundleItems` field to
  // check directly, so this reads off `productData` (`ProductModel`) instead.
  const showDiscountWithoutExpiration = !!productData && isBundle(productData);
  // Mirror the addon rows below: for subscription products, resolve whichever plan is
  // currently selected to its own entry in `data.addons` so the header's price/lastPrice/
  // discount all come from the same place the addon list does, instead of being re-derived.
  const selectedSubscriptionAddon = productData?.isSubscription
    ? data?.addons?.find(
        (addon) =>
          (addon.type === ADDONS_ENUM.SUBSCRIPTION_LIFETIME ||
            addon.type === ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR) &&
          addons[addon.type]?.selected,
      )
    : undefined;
  const [finalPrice, setFinalPrice] = useState(0);
  const [productPrice, setProductPrice] = useState(0);
  const { mutate } = usePostAddToCart();
  const { mutate: Remove } = useRemoveFromCart();
  const unselectedPrerequisites = Object.keys(prerequisites).filter(
    (id) => !prerequisites[id].selected,
  );
  const [price, setPrice] = useState(0);
  const [lastPrice, setLastPrice] = useState(0);
  const { setShowSmartOffer, showSmartOffer } = useSmartOfferStore();
  const { data: offer } = useGetSmartOffer('/cart-page');
  useEffect(() => {
    if (offer && (!Array.isArray(offer) || offer.length > 0)) {
      setShowSmartOffer(true);
    }
  }, [offer]);
  useEffect(() => {
    if (data) {
      if (data.addons && data.addons.length > 0) {
        data.addons.forEach(({ type }) => {
          if (
            (type === ADDONS_ENUM.SUBSCRIPTION_LIFETIME && addons[type]?.selected) ||
            (type === ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR && addons[type]?.selected)
          ) {
            setInitialAddonsDisplay(true);
          }
        });
      }
    }
  }, []);
  useEffect(() => {
    if (data) {
      const basePrice = defaultPricing?.price ?? data.price;
      const baseLastPrice = defaultPricing?.lastPrice ?? data.lastPrice;
      const baseRegularPrice = productData?.regularPrice ?? data.regularPrice;

      if (selectedSubscriptionAddon) {
        setPrice(selectedSubscriptionAddon.price);
        setLastPrice(
          selectedSubscriptionAddon.type === ADDONS_ENUM.SUBSCRIPTION_LIFETIME
            ? baseRegularPrice
            : selectedSubscriptionAddon.lastPrice,
        );
      } else if (data.addons && data.addons.length > 0) {
        let finalPrice = basePrice;
        let finalLastPrice = baseLastPrice;
        let addonSelected = false;
        data.addons.forEach(({ type, price, lastPrice }) => {
          if (type === ADDONS_ENUM.SUBSCRIPTION_LIFETIME && addons[type]?.selected) {
            finalPrice = price;
            finalLastPrice = baseRegularPrice;
            addonSelected = true;
          } else if (type === ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR && addons[type]?.selected) {
            finalPrice = price;
            finalLastPrice = lastPrice;
            addonSelected = true;
          }
        });

        if (addonSelected) {
          setPrice(finalPrice);
          setLastPrice(finalLastPrice);
        } else {
          setPrice(basePrice);
          setLastPrice(baseLastPrice);
        }
      } else {
        setPrice(basePrice);
        setLastPrice(baseLastPrice);
      }

      let calculatedPrice = basePrice;
      let calculatedProductPrice = basePrice;
      if (addons[ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR]?.selected) {
        calculatedProductPrice = addons[ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR].price;
        calculatedPrice = addons[ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR].price;
      }
      if (addons[ADDONS_ENUM.SUBSCRIPTION_LIFETIME]?.selected) {
        calculatedProductPrice = addons[ADDONS_ENUM.SUBSCRIPTION_LIFETIME].price;
        calculatedPrice = addons[ADDONS_ENUM.SUBSCRIPTION_LIFETIME].price;
      }
      if (addons[ADDONS_ENUM.REPEAT_PURCHASE]?.selected && baseLastPrice > 0) {
        calculatedPrice = baseLastPrice;
      }

      for (const [key, value] of Object.entries(addons)) {
        if (
          value.selected &&
          key !== ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR &&
          key !== ADDONS_ENUM.SUBSCRIPTION_LIFETIME
        ) {
          calculatedPrice = calculatedPrice + value.price;
        }
      }
      if (Object.keys(prerequisites).length !== 0) {
        for (const prerequisite of Object.values(prerequisites)) {
          if (!prerequisite.selected) {
            calculatedPrice -= prerequisite.price;
            calculatedProductPrice -= prerequisite.price;
          }
        }
        setProductPrice(calculatedProductPrice);
      }

      setFinalPrice(calculatedPrice);
    }
  }, [data, addons, productData, selectedSubscriptionAddon]);
  // The add/remove mutations are mutually exclusive (`isUpdatingAddonsRef` guards against firing
  // one while the other is still in flight). A call blocked by the mutex is queued here rather
  // than dropped — dropping it used to lose e.g. a quick check-then-uncheck's follow-up call if
  // it landed before the check's own request had resolved.
  const pendingAddonUpdateRef = useRef<
    | { type: 'add'; productId: string; unselectedPrerequisites: string[]; key: string }
    | { type: 'remove'; productId: string; key: string }
    | null
  >(null);

  // Broken out via a ref (rather than referencing runAddMutation/runRemoveMutation directly) so
  // the three don't need a circular declaration order — always calls whichever versions were
  // current as of the last render.
  const runPendingAddonUpdateRef = useRef<() => void>(() => {});

  const runAddMutation = useCallback(
    (productId: string, unselectedPrerequisites: string[], key: string) => {
      if (!data) return;
      isUpdatingAddonsRef.current = true;
      mutate(
        {
          id: productId,
          slug: key,
          prerequisite: unselectedPrerequisites,
        },
        {
          onSuccess: (res) => {
            if (res.appliedCodeSteppedDiscount) {
              showToast({ message: res.appliedCodeSteppedDiscount, type: 'default' });
            }
            setSteppedDiscount(res.steppedDiscount);
            isUpdatingAddonsRef.current = false;
            runPendingAddonUpdateRef.current();
          },
          onError: () => {
            isUpdatingAddonsRef.current = false;
            runPendingAddonUpdateRef.current();
          },
        },
      );
    },
    [mutate, data],
  );

  const runRemoveMutation = useCallback(
    (productId: string, key: string) => {
      if (!data) return;
      isUpdatingAddonsRef.current = true;
      Remove(
        {
          id: productId,
          slug: key,
        },
        {
          onSuccess: (res) => {
            if (res.appliedCodeSteppedDiscount) {
              showToast({ message: res.appliedCodeSteppedDiscount, type: 'default' });
            }
            setSteppedDiscount(res.steppedDiscount);
            isUpdatingAddonsRef.current = false;
            runPendingAddonUpdateRef.current();
          },
          onError: () => {
            isUpdatingAddonsRef.current = false;
            runPendingAddonUpdateRef.current();
          },
        },
      );
    },
    [Remove, data],
  );

  useEffect(() => {
    runPendingAddonUpdateRef.current = () => {
      const pending = pendingAddonUpdateRef.current;
      if (!pending) return;
      pendingAddonUpdateRef.current = null;
      if (pending.type === 'add') {
        runAddMutation(pending.productId, pending.unselectedPrerequisites, pending.key);
      } else {
        runRemoveMutation(pending.productId, pending.key);
      }
    };
  }, [runAddMutation, runRemoveMutation]);

  const debouncedMutate = useCallback(
    debounce(
      (
        productId: string,
        _newAddonsList: string[],
        unselectedPrerequisites: string[],
        key: string,
      ) => {
        if (!data) return;
        if (isUpdatingAddonsRef.current) {
          pendingAddonUpdateRef.current = {
            type: 'add',
            productId,
            unselectedPrerequisites,
            key,
          };
          return;
        }
        runAddMutation(productId, unselectedPrerequisites, key);
      },
      300, // 300ms delay
    ),
    [data, runAddMutation],
  );
  const debouncedRemoveMutate = useCallback(
    debounce(
      (productId: string, key: string) => {
        if (!data) return;
        if (isUpdatingAddonsRef.current) {
          pendingAddonUpdateRef.current = { type: 'remove', productId, key };
          return;
        }
        runRemoveMutation(productId, key);
      },
      300, // 300ms delay
    ),
    [data, runRemoveMutation],
  );

  const handleAddOnsChange = useCallback(
    (key: string, checked: boolean, price: number) => {
      if (addons[key]?.selected === checked) {
        return;
      }
      const subscriptionTypes = [
        ADDONS_ENUM.SUBSCRIPTION_LIFETIME,
        ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR,
      ];
      const isCurrentAddonSubscription = subscriptionTypes.includes(key as any);
      let updatedAddOns = { ...addons };

      if (isCurrentAddonSubscription) {
        if (checked) {
          Object.keys(addons).forEach((addonKey) => {
            const isOtherSubscription = subscriptionTypes.includes(addonKey as any);
            if (isOtherSubscription && addons[addonKey]?.selected) {
              updatedAddOns[addonKey] = { ...updatedAddOns[addonKey], selected: false };
            }
          });
        } else {
          // Unchecking a subscription plan must always swap to the other plan by POSTing it,
          // exactly like the product page's Sidebar (side-bar/index.tsx:138-198,
          // `subscriptionAddonsDisplay`) — never by calling remove. The Sidebar resolves the
          // *other* plan the same way regardless of whether it has its own live row in the
          // add-ons list: prefer the live row (`data.addons`) when present, otherwise synthesize
          // it from the product's own fields via the same `getOneYearSubscriptionPricing`/
          // `getLifetimePricing` helpers the Sidebar uses for its fallback rows — this is what
          // makes the *default* plan (which often has no live addon row at all, since it's the
          // base configuration rather than something offered as an addition) still resolvable.
          // One-year's fallback always "exists" (it's the base plan, mirroring the Sidebar's
          // `oneYearFallback`/`exists: true`); lifetime's fallback only "exists" when the product
          // has a `regularPrice` configured for it (mirroring the Sidebar's
          // `exists: !!data.regularPrice`) — the one genuinely unreachable case, a product with
          // only one subscription plan total, falls out of this naturally: that lone plan is
          // never rendered as its own checkbox in the first place, so this branch never runs for it.
          const otherType =
            key === ADDONS_ENUM.SUBSCRIPTION_LIFETIME
              ? ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR
              : ADDONS_ENUM.SUBSCRIPTION_LIFETIME;
          const otherFromApi = data?.addons?.find((addon) => addon.type === otherType);

          let otherSubscriptionAddon: { type: typeof otherType; price: number } | null = null;
          if (otherFromApi) {
            if (otherFromApi.price != null) {
              otherSubscriptionAddon = { type: otherType, price: otherFromApi.price };
            }
          } else if (productData) {
            if (otherType === ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR) {
              otherSubscriptionAddon = {
                type: otherType,
                price: getOneYearSubscriptionPricing(productData).price,
              };
            } else if (productData.regularPrice) {
              otherSubscriptionAddon = {
                type: otherType,
                price: getLifetimePricing(productData).price,
              };
            }
          }

          if (otherSubscriptionAddon) {
            updatedAddOns[otherSubscriptionAddon.type] = {
              ...updatedAddOns[otherSubscriptionAddon.type],
              selected: true,
              price: otherSubscriptionAddon.price,
            };
          }
        }
      }

      updatedAddOns[key] = { selected: checked, price: price };
      setAddons(updatedAddOns);

      const newAddonsList = Object.keys(updatedAddOns).filter(
        (item) => updatedAddOns[item].selected,
      );
      const isKeyNowSelected = newAddonsList.includes(key);

      if (isKeyNowSelected) {
        debouncedMutate(productId, newAddonsList, unselectedPrerequisites, key);
      } else {
        const newlyAddedItems = newAddonsList.filter((item) => !addons[item]?.selected);

        if (!isCurrentAddonSubscription) {
          debouncedRemoveMutate(productId, key);
        }
        if (newlyAddedItems.length > 0) {
          newlyAddedItems.forEach((addedItem) => {
            debouncedMutate(productId, newAddonsList, unselectedPrerequisites, addedItem);
          });
        }
      }
    },
    [
      addons,
      data,
      productData,
      debouncedMutate,
      debouncedRemoveMutate,
      productId,
      unselectedPrerequisites,
      setAddons,
    ],
  );
  useEffect(() => {
    if (cart?.products && cart?.products?.length > 0) {
      setAddons({
        ...addons,
        ...cart.products.reduce(
          (acc, item) => {
            if (item.addon && item.addon.relatedId === productId) {
              acc[item?.addon?.slug] = {
                selected: true,
                price: item.price,
              };
            }
            return acc;
          },
          {} as Record<string, { selected: boolean; price: number }>,
        ),
      });
    }
  }, [cart]);
  const hasSmartOffer = !!offer && offer.length !== 0 && showSmartOffer;
  return (
    <Container
      data-cy={DATA_CY_ADD_TO_CARD_POP_UP}
      className='flex h-full min-h-0 flex-col bg-[#F0F0F280] px-[15px] pt-[7px] pb-[15px] md:px-[30px] md:pt-[15px] md:pb-0!'
    >
      <Container className='grid min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)] overflow-hidden rounded-md bg-[#FFFFFF] p-2 pb-[9px] shadow-[0px_15px_25px_0px_#969BA41A]'>
        <Container className='border-b border-dashed border-[#7E899B33] px-3'>
          {isPending ? (
            <ProductSummaryLoading />
          ) : (
            data && (
              <ProductSummary
                {...data}
                price={price}
                lastPrice={price != lastPrice ? lastPrice : undefined}
                discount={
                  selectedSubscriptionAddon
                    ? selectedSubscriptionAddon.discount || 0
                    : (defaultPricing?.discount ?? data.discount)
                }
                isOnSale={productData ? productData.isOnSale : data.isOnSale}
                finalPrice={Object.keys(prerequisites).length > 0 ? productPrice : 0}
                showDiscountWithoutExpiration={showDiscountWithoutExpiration}
              />
            )
          )}
        </Container>
        <Container
          data-cy={DATA_CY_OPTIONS_ITEM_CONTAINER}
          className='orange-scrollbar flex min-h-0 flex-col divide-y divide-dashed divide-[#7E899B33] overflow-y-auto px-3'
        >
          {isPending ? (
            <>
              <OptionsLoading hasCheckbox />
              <OptionsLoading hasCheckbox />
            </>
          ) : (
            data &&
            data.addons &&
            data.addons.length > 0 &&
            data.addons.map((addon) => {
              const { id, type, price, replace, excludes } = addon;
              const isSubscription =
                type === ADDONS_ENUM.SUBSCRIPTION_LIFETIME ||
                type === ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR;
              const isSelected = addons[type]?.selected;

              const displayData = isSubscription && initialAddonsDisplay ? replace : addon;
              const displayPrice =
                type === ADDONS_ENUM.SUBSCRIPTION_LIFETIME && !initialAddonsDisplay
                  ? addon.price
                  : displayData?.price;

              const disabled = isAddonDisabled(type, excludes, addons, data.addons);

              if (
                (type === ADDONS_ENUM.SUBSCRIPTION_LIFETIME && price == null) ||
                (type === ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR && price == null)
              ) {
                return null;
              }
              return (
                <OptionsItem
                  key={id}
                  typeItem={type}
                  label={displayData.title}
                  subTitle={displayData.subTitle}
                  price={displayPrice}
                  lastPrice={
                    displayData?.discount && displayData?.discount > 0
                      ? displayData.lastPrice
                      : 0
                  }
                  discount={displayData.discount || 0}
                  saleEndedAt={displayData.saleEndedAt}
                  hasCheckbox
                  checked={isSelected}
                  onChange={(checked) => handleAddOnsChange(type, checked, price)}
                  disabled={disabled}
                  noSign={isSubscription}
                  showFakeCheckbox={isSubscription && initialAddonsDisplay && isSelected}
                  showFakeChecked={isSubscription && initialAddonsDisplay && !isSelected}
                  className='flex-col items-start md:flex-row md:items-center'
                  priceClassName='ms-auto md:ms-0'
                />
              );
            })
          )}
        </Container>
      </Container>
      <When condition={hasSmartOffer}>
        <Container className='shrink-0'>
          <SmartInLineCart data={offer} />
        </Container>
      </When>
      {steppedDiscount && (
        <SteppedDiscount
          containerClassName='px-0! mt-[1px] shrink-0'
          data={steppedDiscount}
          percentClassName='md:mr-7! md:ml-[120px]! ml-[55px]!'
          amountClassName='md:mr-3! md:ml-[105px]!  ml-[40px]!'
          isModal={true}
        />
      )}
      <Container
        className={cn(
          '-mx-[15px] -mb-[18px] shrink-0 rounded-b-xl bg-[#FFFFFF]! px-[15px] pt-4 pb-[25px] md:-mx-[30px] md:px-[30px] md:pb-10',
          hasSmartOffer ? 'mt-[7px] md:mt-[15px]' : 'mt-[15px] md:mt-[30px]',
        )}
      >
        {isPending ? (
          <OptionsLoading />
        ) : (
          <OptionsItem
            className='rounded-[7px] bg-[linear-gradient(180deg,#84ba521a_0%,rgba(132,186,82,0)_100%)] px-3 pb-1'
            label={productLocalization.payableAmount}
            price={finalPrice}
            noSign
          />
        )}
        <Container center className='justify-between gap-4 pt-3.5'>
          <Button
            data-cy={DATA_CY_RETURN_BUTTON}
            className='h-[52px] w-full max-w-[184px] bg-[#F7F8F9] text-[15px] font-bold whitespace-nowrap! text-[#878F9B]'
            onClick={onClose}
          >
            {productLocalization.continueShopping}
          </Button>
          <a href='/cart' className='w-[184px]'>
            <Button className='h-[52px] w-full !px-1 text-[15px] font-bold !whitespace-nowrap text-white'>
              {productLocalization.billPayment}
              <FaArrowLeftLong size={ICON_SIZE.md} className='pr-2' />
            </Button>
          </a>
        </Container>
      </Container>
    </Container>
  );
}
